import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button, Container, Group, Skeleton, Stack, Text } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconSearch, IconWheat } from '@tabler/icons-react';
import PageTitle from '@/components/common/PageTitle';
import EmptyState from '@/components/common/EmptyState';
import BuyFilterBar from '@/components/app/buy/BuyFilterBar';
import BuyBasisSection from '@/components/app/buy/BuyBasisSection';
import BuyAttestationGate from '@/components/app/buy/BuyAttestationGate';
import { orderService } from '@/api/services/OrderService';
import { BUY_TREE_QUERY_KEY, useBuyTree } from '@/hooks/useBuy';
import { basisKey, useBasisGoodsFilter } from '@/hooks/useBasisGoodsFilter';
import { useProfile } from '@/hooks/useProfile';
import { useAttestationRequired } from '@/hooks/useAttestationRequired';
import { useIsAgent } from '@/hooks/useRoles';
import { flattenBuyTree, groupByBasis, withoutBasis, yearHint, type BasisGroup } from '@/lib/buyTree';
import { getApiMessage, isServiceUnavailable } from '@/api/errors';
import { ROUTES } from '@/routes/paths';
import type { BuyTreeResponse } from '@/types/buy';
import { formatOrgName } from '@/lib/format';

// После удаления прайс-листов устаревают детали прайс-листов, лучшие цены продавцов к ним и «Лучшие предложения» на
// «Главной». Их перезагружаем без ожидания: удалённый раздел убирается из дерева сразу (см. `onSuccess`).
const AFTER_DELETE_KEYS = [['purchasePriceList'], ['buyBestPrices'], ['bestOffers']] as const;


// «Купить» («Бюллетень», как «Продать»): мои прайс-листы на покупку разделами по базису, в разделе — таблица культур
// с диапазоном моих цен (года у закупки нет, решение 2026-11-04). Фильтр по базису и культуре записан в адрес страницы.
export default function BuyListPage() {
  const { t } = useTranslation();
  const { data, isLoading, isError, error, refetch } = useBuyTree();
  const isAgent = useIsAgent(); // агент не публикует прайс-листы (только посредничает по сделкам)

  // Кнопку создания показываем только после загрузки профиля: до неё ограничение аттестации считается снятым, и кнопка
  // мелькнула бы у неаттестованной организации. Сбой профиля кнопку не прячет — форма проверит ограничение сама.
  const profile = useProfile();
  const attestationRequired = useAttestationRequired();
  const canCreate = (profile.data != null || profile.isError) && !isAgent && !attestationRequired;

  const groups = useMemo(() => flattenBuyTree(data), [data]);
  const allBases = useMemo(() => groupByBasis(groups), [groups]);
  const basisOptions = useMemo(
    () =>
      allBases.map((base) => ({
        value: basisKey(base),
        label: `${formatOrgName(base.addressTitle)} — ${base.address}`,
        title: base.addressTitle,
        address: base.address,
      })),
    [allBases],
  );
  const goodsOptions = useMemo(() => [...new Set(groups.map((group) => group.goods))].sort(), [groups]);
  const filter = useBasisGoodsFilter(
    data ? basisOptions.map((option) => option.value) : null,
    data ? goodsOptions : null,
  );

  const filtered = useMemo(
    () => groups.filter((group) => (!filter.basis || basisKey(group) === filter.basis) && (!filter.good || group.goods === filter.good)),
    [groups, filter.basis, filter.good],
  );
  const basisSections = useMemo(() => groupByBasis(filtered), [filtered]);
  const showFilters = allBases.length > 1 || goodsOptions.length > 1;

  // Удаление ВСЕХ прайс-листов базиса: необратимо, поэтому в подтверждении — базис и полный состав.
  const queryClient = useQueryClient();
  const removeBasis = useMutation({
    mutationFn: (basisGroup: BasisGroup) =>
      orderService.deletePurchaseByBasis({ address: basisGroup.address, addressTitle: basisGroup.addressTitle }),
    onSuccess: (_, basisGroup) => {
      // Удалённый раздел исчезает сразу, не дожидаясь перезагрузки дерева: сбой перезагрузки не вернул бы на экран
      // удалённые прайс-листы со ссылками на несуществующие детали.
      queryClient.setQueryData<BuyTreeResponse>(BUY_TREE_QUERY_KEY, (tree) => withoutBasis(tree, basisGroup));
      for (const queryKey of [BUY_TREE_QUERY_KEY, ...AFTER_DELETE_KEYS]) {
        void queryClient.invalidateQueries({ queryKey });
      }
      notifications.show({ color: 'lime', message: t('app.buy.del.deleted') });
      // Пункт меню исчез вместе с разделом: фокус — на заголовок страницы, а не в начало документа.
      requestAnimationFrame(() => document.querySelector<HTMLElement>('#main h1')?.focus());
    },
    onError: (err) => {
      notifications.show({
        color: 'red',
        message: isServiceUnavailable(err) ? t('auth.errors.unavailable') : (getApiMessage(err) ?? t('auth.errors.generic')),
      });
    },
  });

  // Сервер удаляет все прайс-листы базиса, поэтому в подтверждении — полный состав, а не только видимый при фильтре.
  function confirmDeleteBasis(visible: BasisGroup): void {
    const basisGroup = allBases.find((item) => basisKey(item) === basisKey(visible)) ?? visible;
    const basis = formatOrgName(basisGroup.addressTitle) || basisGroup.address;
    const list = basisGroup.items
      .map((group) => [group.goods, yearHint(group, basisGroup.items)].filter((part) => part != null).join(' '))
      .join(', ');
    modals.openConfirmModal({
      title: t('app.buy.del.basisTitle'),
      children: <Text size="sm">{t('app.buy.del.basisConfirm', { count: basisGroup.items.length, basis, list })}</Text>,
      labels: { confirm: t('app.buy.del.confirm'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => removeBasis.mutate(basisGroup),
    });
  }
  const deletingKey = removeBasis.isPending && removeBasis.variables ? basisKey(removeBasis.variables) : null;

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        {/* wrap: на узком экране кнопки действий переносятся под заголовок, а не сжимают его в столбик. */}
        <Group justify="space-between" wrap="wrap" gap="md" align="flex-start">
          <Stack gap={4} style={{ flex: '1 1 260px', minWidth: 0 }}>
            <PageTitle tabIndex={-1}>{t('app.buy.title')}</PageTitle>
            <Text size="sm" c="gray.8">
              {t('app.buy.subtitle')}
            </Text>
          </Stack>
          <Group gap="sm" wrap="wrap" style={{ flexShrink: 0, maxWidth: '100%' }}>
            {/* Поиск рынка связан с закупкой — держим вход в «Лучшую цену» прямо в разделе. */}
            <Button component={Link} to={ROUTES.APP_BEST_PRICE} variant="default" leftSection={<IconSearch size={16} />}>
              {t('app.nav.bestPrice')}
            </Button>
            {groups.length > 0 && canCreate ? (
              <Button component={Link} to={ROUTES.APP_BUY_NEW} leftSection={<IconPlus size={16} />}>
                {t('app.buy.newPriceList')}
              </Button>
            ) : null}
          </Group>
        </Group>

        {attestationRequired && !isAgent ? <BuyAttestationGate /> : null}

        {/* Ошибка во весь блок — только когда показывать нечего; при сбое обновления список остаётся, ошибка над ним. */}
        {isError ? (
          <Group gap="md">
            <Text c="loss.8">{isServiceUnavailable(error) ? t('auth.errors.unavailable') : t('auth.errors.generic')}</Text>
            <Button variant="subtle" size="compact-sm" onClick={() => refetch()}>
              {t('common.retry')}
            </Button>
          </Group>
        ) : null}

        {isError && !data ? null : isLoading ? (
          // Скелет повторяет будущую разметку: заголовок раздела и строки таблицы.
          <Stack gap={8} aria-busy="true" aria-label={t('app.buy.loading')}>
            <Skeleton h={24} w={260} />
            <Skeleton h={36} />
            <Skeleton h={36} />
            <Skeleton h={36} />
          </Stack>
        ) : groups.length === 0 ? (
          isAgent ? (
            <EmptyState decorated={false} icon={<IconWheat size={30} />} title={t('app.buy.agentTitle')} description={t('app.buy.agentHint')} />
          ) : (
            <EmptyState
              decorated={false}
              icon={<IconWheat size={30} />}
              title={t('app.buy.empty')}
              description={t('app.buy.emptyHint')}
              action={
                canCreate ? (
                  <Button component={Link} to={ROUTES.APP_BUY_NEW} leftSection={<IconPlus size={16} />}>
                    {t('app.buy.create')}
                  </Button>
                ) : undefined
              }
            />
          )
        ) : (
          <Stack gap="md">
            {showFilters ? (
              <Group justify="space-between" wrap="wrap" align="center" gap="sm">
                <BuyFilterBar
                  bases={basisOptions}
                  goods={goodsOptions}
                  basis={filter.basis}
                  good={filter.good}
                  onBasis={(value) => {
                    const option = basisOptions.find((item) => item.value === value);
                    filter.setBasis(option ? { address: option.address, addressTitle: option.title } : null);
                  }}
                  onGood={filter.setGood}
                />
                <Text size="sm" c="gray.8" role="status">
                  {t('app.buy.bases', { count: basisSections.length })} · {t('app.buy.priceLists', { count: filtered.length })}
                </Text>
              </Group>
            ) : null}
            {basisSections.length === 0 ? (
              <Group gap="xs">
                <Text c="gray.7" size="sm">
                  {t('app.buy.nothingFound')}
                </Text>
                <Button variant="subtle" size="compact-sm" onClick={filter.reset}>
                  {t('app.buy.resetFilters')}
                </Button>
              </Group>
            ) : (
              <Stack gap={34}>
                {basisSections.map((basisGroup) => (
                  <BuyBasisSection
                    key={basisKey(basisGroup)}
                    basis={basisGroup}
                    onDelete={isAgent ? undefined : () => confirmDeleteBasis(basisGroup)}
                    deleting={deletingKey === basisKey(basisGroup)}
                    menuDisabled={removeBasis.isPending}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </Container>
  );
}
